/**
 * Deal rows as the drawer sees them — only the fields this merge reads.
 */
export type DealRow = {
  id?: unknown;
  updatedAt?: unknown;
  stageChange?: unknown;
  [key: string]: unknown;
};

/** Later of `updatedAt` / `stageChange` — the record that saw the last write. */
function dealFreshness(c: DealRow | null | undefined): number {
  const t = (v: unknown) => {
    const n = v ? new Date(String(v)).getTime() : NaN;
    return Number.isFinite(n) ? n : 0;
  };
  return Math.max(t(c?.updatedAt), t(c?.stageChange));
}

/**
 * Union API + lead.child so new deals show before /child list refetch (see
 * lead stage update). When BOTH sources carry a deal, the FRESHER record wins
 * (by `updatedAt` / `stageChange`), not the API list unconditionally: the two
 * caches refresh at different moments after a stage move, and letting a
 * stale list override a just-refetched lead payload is how a deal rolled
 * back to Deposit kept showing under Pre Visa (2026-09-07).
 */
export function mergeLeadChildrenWithApi<T>(
  fromApi: T[] | null | undefined,
  fromLead: T[] | null | undefined,
): T[] {
  const api = fromApi ?? [];
  const leadC = fromLead ?? [];
  if (!api.length) return leadC;
  if (!leadC.length) return api;
  const map = new Map<number, T>();
  const rowOf = (c: T): DealRow | null =>
    c && typeof c === "object" ? (c as unknown as DealRow) : null;
  for (const c of api) {
    const id = rowOf(c)?.id;
    if (id != null) map.set(Number(id), c);
  }
  for (const c of leadC) {
    const row = rowOf(c);
    if (row?.id == null) continue;
    const id = Number(row.id);
    const existing = map.get(id);
    if (!existing) {
      map.set(id, c);
    } else if (dealFreshness(row) > dealFreshness(rowOf(existing))) {
      // Keep the richer API row's extra fields, but let the fresher lead
      // payload's position (stage/status/sub-status, clocks) win.
      map.set(id, { ...(existing as object), ...(c as object) } as T);
    }
  }
  return Array.from(map.values());
}

